import { useState } from 'react';
import { X, Lock } from 'lucide-react';

interface FolderTagInputProps {
  label: string;
  hint: string;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  // A2F-1035: always-on globs shown for context but NOT editable (e.g. the
  // system core `.*` / `~$*`). Rendered as muted, non-removable chips before
  // the editable ones. Excluded from the rule count and from dedupe.
  lockedValues?: string[];
}

// Shared chip/tag input for folder-name rule lists. Used by Project Settings
// (Ignore Folders, Working Folders) and the FRE wizard's Advanced Settings.
//
// A2F-770 / A2F-772: commit() always splits on commas so a paste or Tab-commit
// of "A, B, C" yields three chips instead of one. Dedupes case-insensitively
// against existing values and prior additions in the same commit.
export function FolderTagInput({
  label,
  hint,
  values,
  onChange,
  disabled,
  placeholder,
  lockedValues,
}: FolderTagInputProps) {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const segments = draft.split(',').map((s) => s.trim()).filter(Boolean);
    if (segments.length === 0) return;
    const existing = new Set(values.map((v) => v.toLowerCase()));
    const additions: string[] = [];
    for (const seg of segments) {
      const key = seg.toLowerCase();
      if (existing.has(key)) continue;
      additions.push(seg);
      existing.add(key);
    }
    if (additions.length > 0) onChange([...values, ...additions]);
    setDraft('');
  };

  const remove = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-semibold text-brand-charcoal">{label}</label>
        <span className="text-[10px] text-slate-400">{values.length} {values.length === 1 ? 'rule' : 'rules'}</span>
      </div>
      <p className="text-[11px] text-slate-500 leading-snug">{hint}</p>
      <div className={`flex flex-wrap items-center gap-1.5 min-h-[38px] px-2 py-1.5 border border-slate-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-brand-orange/30 focus-within:border-brand-orange${disabled ? ' opacity-50' : ''}`}>
        {(lockedValues ?? []).map((v) => (
          <span
            key={`locked-${v}`}
            data-testid="folder-tag-locked"
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-medium rounded-md border border-slate-200"
            title="Always ignored — system rule (can't be removed)"
          >
            <Lock className="w-2.5 h-2.5" />
            {v}
          </span>
        ))}
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-brand-charcoal text-xs font-medium rounded-md border border-orange-100"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              className="text-slate-400 hover:text-red-500 disabled:cursor-not-allowed"
              title={`Remove ${v}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && !draft && values.length > 0) {
              remove(values.length - 1);
            }
          }}
          onBlur={commit}
          disabled={disabled}
          placeholder={values.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[80px] text-sm bg-transparent border-none outline-none placeholder:text-slate-300 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}
